// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useReducer, useRef, useState, type RefObject } from 'react';

import { api } from '../../api/client';
import { useAuth } from '../../app/AuthGate';
import { measureKey } from '../../components/bygning/settings/settingsStorage';
import {
  measureReducer,
  restoreMeasurements,
  storeMeasurements,
  type MeasureAction,
  type MeasureState,
  type MeasureStorage,
  type MeasureTool,
  type Vec3,
} from '../../bygning/ruler';
import {
  SNAP_RADIUS_PX,
  featureCandidates,
  rankSnap,
  type SnapCandidate,
  type SnapFeature,
  type SnapKind,
} from '../../bygning/snapping';
import type { ViewportHandle } from '../../viewport/Viewport';

/** Where the pointer would put a point: the world point and what it snapped to (null: nothing). */
export interface SnapResult {
  world: Vec3;
  kind: SnapKind | null;
}

/** Two clicks this close in time (ms) and space (px) are a double-click: finish. */
const DOUBLE_MS = 400;
const DOUBLE_PX = 6;
/** The camera still for this long (ms) before the pointer is probed again. */
const CAMERA_IDLE_MS = 120;

function sessionStore(): MeasureStorage | undefined {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
  } catch {
    return undefined;
  }
}

/** Whether a key press belongs to a field, dialog or menu rather than the model. */
function inField(event: KeyboardEvent): boolean {
  const target = event.target instanceof HTMLElement ? event.target : null;
  return target !== null && target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]') !== null;
}

export interface MeasureToolOptions {
  viewport: RefObject<ViewportHandle | null>;
  /** The element around the canvas: its pointer moves drive the hover. */
  host: RefObject<HTMLElement | null>;
  /** The tools can be used here (a model on screen, nothing covering it). */
  available: boolean;
  /** The locked plan: points lie on `planZ`, lengths in x/y. */
  plan: boolean;
  /** The plan's floor (world z): the storey's, else the model's lowest. */
  planZ: number;
  /** The building model's snap features for this view (`bimFeatures`). */
  features: readonly SnapFeature[];
}

export interface MeasureController {
  state: MeasureState;
  dispatch: (action: MeasureAction) => void;
  /** The tool on and usable now. */
  active: boolean;
  setTool: (tool: MeasureTool) => void;
  /** Under the pointer while a tool is on (or a Placér 360° drag snaps). */
  hover: SnapResult | null;
  /** A click on the canvas (Viewport `onCanvasClick`): add a point, or finish on a double-click. Always consumed. */
  onCanvasClick: (x: number, y: number) => boolean;
  /** Where canvas pixel (x, y) would put a point; `free` (Alt) skips the snapping. */
  snapAt: (x: number, y: number, free: boolean) => SnapResult | null;
  /** Show (or clear) the snap indicator, for a drag. */
  setHover: (hover: SnapResult | null) => void;
  /**
   * Placér 360°: a plan point (world x/y) snapped to the building model's
   * features within reach on screen; the indicator follows. Alt (held now) skips it.
   */
  snapPlanXY: (xy: readonly [number, number], show: boolean) => [number, number];
  /** A vertex clicked (no drag): finish on the draft's last (or an area's first) point, else measure from it. */
  vertexClick: (id: string, index: number) => void;
  /** A vertex dragged to `world` (a plan measurement keeps its floor). */
  moveVertex: (id: string, index: number, world: Vec3) => void;
  /** Whether Alt / Option is held (no snapping). */
  altHeld: () => boolean;
}

/**
 * Mål afstand / Mål areal (frontend-aa2) on the Ground: the measurements
 * (kept for the case's session, `bygning/ruler.ts`), the snapping under the
 * pointer (`bygning/snapping.ts` over the scene's `probeSnap`), and the
 * keys — Enter or a double-click finishes, Backspace takes the last point
 * back, Esc drops the draft (then the tool), Alt held measures without
 * snapping. Read-only: a viewer may measure too.
 */
export function useMeasureTool({ viewport, host: hostRef, available, plan, planZ, features }: MeasureToolOptions): MeasureController {
  // User- and case-scoped, under the viewer prefix: signing out clears it (`clearViewerStorage`).
  const user = useAuth()?.me.user.id ?? null;
  const caseKey = measureKey(user, api.caseId ?? 'lokal');
  const [state, dispatch] = useReducer(measureReducer, caseKey, (key) => restoreMeasurements(key, sessionStore()));
  const [hover, setHover] = useState<SnapResult | null>(null);
  const active = available && state.tool !== null;

  // Another case: its own measurements.
  const loadedKey = useRef(caseKey);
  useEffect(() => {
    if (loadedKey.current === caseKey) return;
    loadedKey.current = caseKey;
    dispatch({ type: 'load', state: restoreMeasurements(caseKey, sessionStore()) });
  }, [caseKey]);
  // The finished ones are kept for the session (never sent anywhere).
  // Keyed on the items only: on a case switch the old case's items must not
  // land under the new key before its own are loaded.
  useEffect(() => {
    storeMeasurements(loadedKey.current, state.items, sessionStore());
  }, [state.items]);

  const optsRef = useRef({ plan, planZ, features });
  optsRef.current = { plan, planZ, features };
  const alt = useRef(false);

  const snapAt = useCallback(
    (x: number, y: number, free: boolean): SnapResult | null => {
      const handle = viewport.current;
      if (!handle) return null;
      const { plan: inPlan, planZ: z, features: feats } = optsRef.current;
      const flat = (p: Vec3): Vec3 => (inPlan ? [p[0], p[1], z] : p);
      const probe = handle.probeSnap(x, y, SNAP_RADIUS_PX);
      const planHit = () => {
        const hit = handle.planPoint(x, y, z);
        return hit ? { world: [hit[0], hit[1], z] as Vec3, kind: null } : null;
      };
      if (!probe) return inPlan ? planHit() : null;
      // The visible surface's depth: what hides a BIM feature behind it (3D only).
      let surfaceDepth: number | null = null;
      if (!inPlan) {
        for (const d of [probe.mesh?.depth, probe.cloud?.depth]) {
          if (d !== undefined && (surfaceDepth === null || d < surfaceDepth)) surfaceDepth = d;
        }
      }
      if (!free) {
        // In 3D what the clipping box cuts away does not snap; the plan shows every wall from above.
        const project = (xyz: Float64Array) => handle.projectWorld(xyz, { clip: !inPlan });
        const candidates: SnapCandidate[] = [
          ...featureCandidates(feats, [x, y], probe.ray, project, SNAP_RADIUS_PX, 'bim'),
          ...(probe.mesh ? featureCandidates(probe.mesh.features, [x, y], probe.ray, project, SNAP_RADIUS_PX, 'mesh') : []),
          ...(probe.cloud ? [{ kind: 'punkt' as const, source: 'cloud' as const, world: probe.cloud.world, px: probe.cloud.px, depth: probe.cloud.depth }] : []),
        ];
        const best = rankSnap(candidates, SNAP_RADIUS_PX, surfaceDepth);
        if (best) return { world: flat(best.world), kind: best.kind };
      }
      if (inPlan) return planHit();
      // 3D without a snap: the surface under the pointer — the mesh, or the scan.
      if (probe.mesh && (!probe.cloud || probe.mesh.depth <= probe.cloud.depth)) return { world: probe.mesh.world, kind: null };
      if (probe.cloud) return { world: probe.cloud.world, kind: null };
      return null;
    },
    [viewport],
  );

  // --- hover: one probe a frame at most ---------------------------------------

  const pointer = useRef<{ x: number; y: number } | null>(null);
  const frame = useRef<number | null>(null);
  const wait = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A probe walks every cloud point under the pick cylinder: on a big scan
  // it can cost more than a frame, so the next waits twice as long as the
  // last took (at most one in flight, never queued up).
  const lastCost = useRef(0);
  const refreshHover = useCallback(() => {
    if (frame.current !== null || wait.current !== null) return;
    const run = () => {
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        const at = pointer.current;
        const t0 = performance.now();
        setHover(at ? snapAt(at.x, at.y, alt.current) : null);
        lastCost.current = performance.now() - t0;
        // For profiling on a big scan (devtools: the Ground's data-probe-ms).
        if (at && hostRef.current) hostRef.current.dataset.probeMs = lastCost.current.toFixed(1);
      });
    };
    if (lastCost.current > 12) {
      wait.current = setTimeout(() => {
        wait.current = null;
        run();
      }, lastCost.current * 2);
    } else run();
  }, [snapAt, hostRef]);
  const idle = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      if (wait.current !== null) clearTimeout(wait.current);
      if (idle.current !== null) clearTimeout(idle.current);
    },
    [],
  );

  useEffect(() => {
    if (!active) {
      pointer.current = null;
      setHover(null);
      return;
    }
    const host = hostRef.current;
    const canvas = host?.querySelector('canvas') ?? null;
    if (!host) return;
    // The camera moving (an orbit, a pan, the wheel, the controls' damping):
    // no probing until it has been still for a moment — the indicator
    // follows its world point meanwhile (MeasureLayer reprojects it).
    let moving = false;
    const settle = () => {
      if (idle.current !== null) clearTimeout(idle.current);
      idle.current = setTimeout(() => {
        idle.current = null;
        moving = false;
        if (pointer.current) refreshHover();
      }, CAMERA_IDLE_MS);
    };
    const onMove = (event: PointerEvent) => {
      alt.current = event.altKey;
      const rect = (canvas ?? host).getBoundingClientRect();
      pointer.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      // A button down is an orbit or a pan: no probing on the way (the camera settling probes again).
      if (event.buttons !== 0 || moving) return;
      refreshHover();
    };
    const onLeave = () => {
      pointer.current = null;
      setHover(null);
    };
    host.addEventListener('pointermove', onMove);
    host.addEventListener('pointerleave', onLeave);
    const offCamera = viewport.current?.onCameraChange(() => {
      moving = true;
      settle();
    });
    // The BIM meshes' BVHs, built now rather than on the first probe.
    viewport.current?.prepareSnap();
    return () => {
      host.removeEventListener('pointermove', onMove);
      host.removeEventListener('pointerleave', onLeave);
      offCamera?.();
    };
  }, [active, viewport, hostRef, refreshHover]);

  // --- clicks -------------------------------------------------------------------

  const lastClick = useRef<{ t: number; x: number; y: number } | null>(null);
  const onCanvasClick = useCallback(
    (x: number, y: number): boolean => {
      const now = performance.now();
      const prev = lastClick.current;
      lastClick.current = { t: now, x, y };
      if (prev && now - prev.t < DOUBLE_MS && Math.hypot(x - prev.x, y - prev.y) <= DOUBLE_PX) {
        lastClick.current = null;
        dispatch({ type: 'finish' });
        return true;
      }
      const hit = snapAt(x, y, alt.current);
      if (hit) dispatch({ type: 'add', point: hit.world, plan: optsRef.current.plan });
      return true;
    },
    [snapAt],
  );

  // --- keys ---------------------------------------------------------------------

  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Alt') return;
      alt.current = event.type === 'keydown';
      if (active && pointer.current) refreshHover();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    const onBlur = () => {
      alt.current = false;
    };
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
      window.removeEventListener('blur', onBlur);
    };
  }, [active, refreshHover]);

  useEffect(() => {
    if (!active) return;
    // Capture phase, before the shell's own Esc (clear the selection).
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || inField(event)) return;
      const draft = stateRef.current.draft;
      if (event.key === 'Escape') {
        if (draft) dispatch({ type: 'cancel' });
        else dispatch({ type: 'tool', tool: null });
      } else if (event.key === 'Enter' && draft) dispatch({ type: 'finish' });
      else if (event.key === 'Backspace' && draft) dispatch({ type: 'undo' });
      else return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [active]);

  // Out of reach (a panorama, Rapport, a centre tile): a half-drawn measurement goes; the kept ones stay.
  useEffect(() => {
    if (!available && stateRef.current.draft) dispatch({ type: 'cancel' });
  }, [available]);

  const setTool = useCallback((tool: MeasureTool) => dispatch({ type: 'tool', tool }), []);

  const snapPlanXY = useCallback(
    (xy: readonly [number, number], show: boolean): [number, number] => {
      const handle = viewport.current;
      const { planZ: z, features: feats } = optsRef.current;
      if (!handle || alt.current || feats.length === 0) {
        if (show) setHover(null);
        return [xy[0], xy[1]];
      }
      const screen = handle.projectWorld(new Float64Array([xy[0], xy[1], z]), { clip: false });
      if (!Number.isFinite(screen[0]) || !Number.isFinite(screen[1])) return [xy[0], xy[1]];
      const ray = { origin: [xy[0], xy[1], z + 100] as Vec3, dir: [0, 0, -1] as Vec3 };
      const project = (xyz: Float64Array) => handle.projectWorld(xyz, { clip: false });
      const best = rankSnap(featureCandidates(feats, [screen[0], screen[1]], ray, project, SNAP_RADIUS_PX, 'bim'), SNAP_RADIUS_PX);
      if (show) setHover(best ? { world: [best.world[0], best.world[1], z], kind: best.kind } : null);
      return best ? [best.world[0], best.world[1]] : [xy[0], xy[1]];
    },
    [viewport],
  );

  const altHeld = useCallback(() => alt.current, []);

  // A plan measurement's point stays on its own floor; a 3D one moves in 3D.
  // Only the current view's measurements are edited (MeasureLayer shows no handles on the others).
  const moveVertex = useCallback((id: string, index: number, world: Vec3) => {
    const { draft, items } = stateRef.current;
    const m = draft?.id === id ? draft : items.find((x) => x.id === id);
    if (!m || m.plan !== optsRef.current.plan) return;
    const point: Vec3 = m.plan ? [world[0], world[1], m.points[index]?.[2] ?? world[2]] : world;
    dispatch({ type: 'move', id, index, point });
  }, []);

  // Switching between the plan and 3D finishes the measurement in hand: its points are of the other kind.
  const seenPlan = useRef(plan);
  useEffect(() => {
    if (seenPlan.current === plan) return;
    seenPlan.current = plan;
    dispatch({ type: 'finish' });
  }, [plan]);

  const vertexClick = useCallback((id: string, index: number) => {
    const { draft, items } = stateRef.current;
    if (draft && draft.id === id) {
      const last = index === draft.points.length - 1;
      const closes = draft.kind === 'area' && index === 0 && draft.points.length >= 3;
      if (last || closes) dispatch({ type: 'finish' });
      return;
    }
    // Another measurement's point: the next point lands exactly on it.
    const m = items.find((x) => x.id === id);
    if (!m || m.plan !== optsRef.current.plan) return;
    const point = m.points[index];
    if (point) dispatch({ type: 'add', point, plan: m.plan });
  }, []);

  return { state, dispatch, active, setTool, hover, onCanvasClick, snapAt, setHover, snapPlanXY, altHeld, vertexClick, moveVertex };
}
